import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import moment from 'moment';
import { ConfigConstants } from '../../../config/config-constants';
import { ODataObject } from '../../../interfaces/odata-model.interface';
import { ConfigService } from '../../../services/config/config.service';
import { CustomInjector } from '../../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../../services/model-options-loader/model-options-loader.service';
import { compact } from '../../../utilities/compact';
import { CompositeModel } from '../../complex/composite.model';
import { MultilineTextModelOptions } from '../../options/multiline-text-model-options';
import { InternalInfoMultilineTextModel } from '../../primitive/internal-info-multiline-text.model';
import { ODataInternalInfoCategoryModel } from './internal-info-category.model';
import InternalInfoTextRegex = ConfigConstants.internalInfoTextLineRegex;
import INTERNAL_INFO_CATEGORY = ConfigConstants.INTERNAL_INFO_CATEGORY;

export class CategoryModel extends CompositeModel {
    public isEdited = false;
    public textLine: InternalInfoMultilineTextModel;
    public headerInfo = '';
    public gid: string;
    public isInvalid = false;
    public isContentAvailable = false;
    public constructor(
        name: string,
        value = '',
        public functionalLoc: string,
        public textId: string,
        public icon?: IconDefinition,
        public label?: string,
        public title?: INTERNAL_INFO_CATEGORY,
        options?: MultilineTextModelOptions,
    ) {
        super('IntInfDeep');
        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigService);
        this.gid = configService.getUserFrontId();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);

        if (!options) {
            options = new MultilineTextModelOptions({
                rows: 22,
                lineLength: 132,
                formatMultilineTextPattern: configService.getFormatInternalInfoMultilineTextPattern(),
            });
        }
        this.textLine = <InternalInfoMultilineTextModel>(
            this.set(new InternalInfoMultilineTextModel('IntInfDeep', value, functionalLoc, textId, options))
        );
        this.headerInfo = this.getRecentUpdatedHeaderInfo();
        this.updateTextLine();
        // For contact, we are loading regex from gateway, TextId  will starts with "ZC" i.e. "ZC01" to "ZC07"
        if (this.textId.startsWith('ZC')) {
            modelOptionsLoader.load(this.textLine, 'InternalInfo', name);
        } else {
            this.isContentAvailable = this.textLine.value.length > 0;
        }
        this.isInvalid = this.textLine.options.required && this.textLine.value.length === 0;
    }

    public updateTextLine(): void {
        const lines = this.textLine.getLines();
        if (lines.length === 1 && lines[0].match(InternalInfoTextRegex.headerInfo)) {
            this.textLine.value = '';
        }
    }

    /**
     * Header info : "07.12.1992 by Z0042DNE" string that matches "Date by Gid: is header info
     * If header info is already present for current date then return true or false if for past
     */
    public isHeaderInfoAlreadyPresent(): boolean {
        if (!this.textLine.value || this.textLine.value.endsWith('\n')) {
            return false;
        }
        const lines = this.textLine.getLines();
        const headerInfoLength = 22;

        // On edit click, we add new line i.e ("\n") in the end of the text to focus,
        // we have to check, if user added any Value/Text in new line or just edited previous text
        // If user just edited previous text, then we do remove the new line
        let lastString = lines[lines.length - 1];
        if (!lastString) {
            lines.pop();
        }

        lastString = lines[lines.length - 1];
        this.headerInfo = lastString;

        const date = compact(lastString.match(InternalInfoTextRegex.date));
        const gid = compact(lastString.match(InternalInfoTextRegex.gid));

        // The length of header info i.e '07.12.1992 by Z0042DNE' is 22
        // If the Header info is already updated with same user and with current date then return true as it is updated already
        return (
            lastString.length === headerInfoLength &&
            moment(date[0], 'DD.MM.YYYY').isSame(moment(), 'days') &&
            gid[0] === this.gid
        );
    }

    /**
     * There will be multiple header info in multiLine text
     * we have to extract the latest updated header info using moment max date
     */
    public getRecentUpdatedHeaderInfo(): string {
        const lines = this.textLine.getLines().filter((info) => info.match(InternalInfoTextRegex.date));
        if (lines.length < 1) {
            return '';
        }
        const dateList = lines.map((d) => moment(d, 'DD.MM.YYYY'));
        const date = moment.max(dateList).format(ConfigConstants.formatPatternDateGeneric);

        // The format of header info is fixed i.e. "DD.MM.YYYY by userGid" so the header info should match the regex
        const headerInfo = lines.filter(
            (textLine) => textLine.includes(date) && textLine.match(InternalInfoTextRegex.headerInfo),
        );

        // After filter, the headerInfo will have array of headerInfo, example: ['08.04.2022 by Z0042DNE', '08.04.2022 by Z005SAS']
        // The data we get from backend is already sorted so we have always have to take last value : ['08.04.2022 by Z005SAS'] is picked
        return headerInfo[headerInfo.length - 1];
    }

    public getOutModel(): ODataObject {
        return <ODataInternalInfoCategoryModel>super.getOutModel();
    }
}
